import React, { useCallback, useEffect, useState } from "react";
import { InfoTip } from "@/components/InfoTip";
import { BookOpenCheck, Download, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AdminLedgerEntryForm } from "@/components/AdminLedgerEntryForm";
import { LedgerTable } from "@/components/LedgerTable";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const eur = (v) => `${v.toFixed(2).replace(".", ",")} €`;

const prevMonth = () => {
  const d = new Date();
  d.setDate(0);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

// Admin: livre des recettes (cash received) to copy into the monthly URSSAF declaration.
export function AdminLedger() {
  const init = prevMonth();
  const [year, setYear] = useState(init.year);
  const [month, setMonth] = useState(init.month);
  const [led, setLed] = useState(null);
  const [sending, setSending] = useState(false);
  const period = month ? `${year}-${String(month).padStart(2, "0")}` : `${year}`;

  const load = useCallback(() => {
    api.get("/admin/ledger", { params: { period } }).then(({ data }) => setLed(data)).catch(() => setLed(null));
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette ligne ?")) return;
    try {
      await api.delete(`/admin/ledger/entries/${id}`);
      load();
    } catch {
      toast.error("Échec de la suppression.");
    }
  };

  const download = async (fmt, book = "recettes") => {
    try {
      const { data } = await api.get("/admin/ledger/export", { params: { period, fmt, book }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SUX1_${book}_${period}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Échec du téléchargement.");
    }
  };

  const testEmail = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/urssaf-reminder/test");
      toast.success(data.message);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'envoi.");
    } finally {
      setSending(false);
    }
  };

  const years = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);
  const sel = "border border-border bg-background px-3 py-2 text-sm";
  const btn = "font-mono uppercase tracking-[0.2em] text-[11px] px-4 py-2 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors flex items-center gap-2 disabled:opacity-50";
  return (
    <div className="border border-[#002FA7] p-6 max-w-3xl mt-6" data-testid="admin-ledger-panel">
      <div className="sux-marker mb-2 flex items-center gap-2"><BookOpenCheck className="w-3 h-3" /> Livre des recettes · URSSAF<InfoTip testid="admin-ledger-info" content={"Registre obligatoire en micro-entreprise : chaque vente, date et montant. Le total du mois est le chiffre d'affaires à déclarer à l'URSSAF. Les remboursements sont déduits. Export CSV ou PDF."} /></div>
      <div className="flex flex-wrap gap-2 mb-4">
        <select className={sel} value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="admin-ledger-month-select">
          <option value={0}>Toute l&apos;année</option>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <select className={sel} value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="admin-ledger-year-select">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <AdminLedgerEntryForm onAdded={load} />
      {led && (
        <>
          <div className="bg-[#002FA7] text-white p-4 mb-4" data-testid="admin-ledger-total">
            <div className="font-mono uppercase tracking-[0.2em] text-[10px] opacity-80">Chiffre d&apos;affaires à déclarer · {led.label}</div>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div data-testid="admin-ledger-services-total">
                <div className="text-xs opacity-80">Prestations de services</div>
                <div className="text-2xl font-black" style={{ fontFamily: "Outfit" }}>{eur(led.services_eur)}</div>
              </div>
              <div data-testid="admin-ledger-goods-total">
                <div className="text-xs opacity-80">Ventes de marchandises</div>
                <div className="text-2xl font-black" style={{ fontFamily: "Outfit" }}>{eur(led.goods_eur)}</div>
              </div>
            </div>
            <div className="text-xs opacity-80 mt-2">{led.count} vente(s) encaissée(s), remboursements déduits · total {eur(led.total_eur)}</div>
          </div>
          {!month && led.by_month.length > 0 && (
            <ul className="text-sm mb-4 space-y-1" data-testid="admin-ledger-by-month">
              {led.by_month.map((m) => (
                <li key={m.month}><span className="capitalize">{m.label}</span> : services <b>{eur(m.services_eur)}</b> · marchandises <b>{eur(m.goods_eur)}</b></li>
              ))}
            </ul>
          )}
          <div className="sux-marker mb-2">Recettes</div>
          <LedgerTable rows={led.rows} partyLabel="Client" empty="Aucune recette sur cette période : déclarez 0 €." onDelete={remove} testid="admin-ledger-table" />
          <div className="flex flex-wrap gap-2 mb-6">
            <button type="button" className={btn} onClick={() => download("csv")} data-testid="admin-ledger-csv-btn"><Download className="w-3 h-3" /> Recettes CSV / Excel</button>
            <button type="button" className={btn} onClick={() => download("pdf")} data-testid="admin-ledger-pdf-btn"><Download className="w-3 h-3" /> Recettes PDF</button>
          </div>
          <div className="sux-marker mb-2" data-testid="admin-purchases-total">Registre des achats · {eur(led.purchases_eur)}</div>
          <LedgerTable rows={led.purchases} partyLabel="Fournisseur" empty="Aucun achat sur cette période." onDelete={remove} testid="admin-purchases-table" />
          <div className="flex flex-wrap gap-2 mb-6">
            <button type="button" className={btn} onClick={() => download("csv", "achats")} data-testid="admin-purchases-csv-btn"><Download className="w-3 h-3" /> Achats CSV / Excel</button>
            <button type="button" className={btn} onClick={() => download("pdf", "achats")} data-testid="admin-purchases-pdf-btn"><Download className="w-3 h-3" /> Achats PDF</button>
          </div>
        </>
      )}
      <div className="flex flex-wrap gap-2">
        <button type="button" className={btn} onClick={testEmail} disabled={sending} data-testid="admin-ledger-test-email-btn">
          {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} M&apos;envoyer le rappel maintenant
        </button>
      </div>
      <p className="mt-3 text-xs text-muted-foreground font-mono">
        Rappel automatique par email 7 jours puis 2 jours avant la fin de chaque mois, avec le chiffre d&apos;affaires du mois précédent.
      </p>
    </div>
  );
}

export default AdminLedger;
